// One lead in the Lead Inspector, on GET /admin/leads/{id}/inspect (backend:
// leads/inspector_api.py + leads/lead_inspector.py). The URL is the lead, so
// it can be pasted into a ticket or a chat as is.
//
// Everything here is shown as the backend sent it: consumer data arrives
// masked, a hidden buyer arrives as "Other buyer", an event that mentions
// one arrives without its payload. This screen adds nothing to any of it.
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, SearchX } from 'lucide-react'
import api from '../../services/api'
import LeadHeader from './LeadHeader'
import Timeline from './Timeline'
import BuyerResponses from './BuyerResponses'
import { EventLog, Outbound, RawPayload, RelatedLeads, SubmittedFields, TrackingConsent } from './DetailSections'
import { FALLBACK_TIMEZONE, copyText, detailEndpoint, fmtJson } from './model'

export default function InspectorDetail({ leadId }) {
  const validId = /^\d+$/.test(String(leadId || ''))
  const [state, setState] = useState({ data: null, error: null, loading: validId })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!validId) return undefined
    let cancelled = false
    setState({ data: null, error: null, loading: true })
    api.get(detailEndpoint(leadId))
      .then(({ data }) => { if (!cancelled) setState({ data, error: null, loading: false }) })
      .catch((error) => { if (!cancelled) setState({ data: null, error, loading: false }) })
    return () => { cancelled = true }
  }, [leadId, validId, tick])

  const notFound = !validId || state.error?.response?.status === 404
  if (notFound) return <NotFound leadId={leadId} />
  if (state.error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackLink />
        <section className="card" role="alert" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)' }}>Lead #{leadId} could not be loaded.</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setTick((t) => t + 1)}>Retry</button>
        </section>
      </div>
    )
  }
  if (!state.data) return <Skeleton />

  const data = state.data
  const timezone = FALLBACK_TIMEZONE

  const download = () => {
    const blob = new Blob([fmtJson(data)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lead-${data.header.lead_id}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <LeadHeader
        header={data.header}
        timezone={timezone}
        onCopyLink={() => copyText(window.location.href, 'Link copied')}
        onDownload={download}
      />
      <div className="li-grid">
        <Timeline steps={data.timeline || []} timezone={timezone} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <BuyerResponses buyers={data.buyers || []} />
          <Outbound outbound={data.outbound} timezone={timezone} />
        </div>
      </div>
      <div className="li-grid">
        <SubmittedFields fields={data.fields} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <TrackingConsent tracking={data.tracking} timezone={timezone} />
          <RelatedLeads related={data.related || []} timezone={timezone} />
        </div>
      </div>
      <EventLog events={data.event_log || []} timezone={timezone} />
      <RawPayload payload={data.raw_payload} />
    </div>
  )
}

function BackLink() {
  return (
    <Link to="/lead-inspector" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none', width: 'fit-content' }}>
      <ArrowLeft size={14} aria-hidden="true" /> Lead Inspector
    </Link>
  )
}

function NotFound({ leadId }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BackLink />
      <section className="card" role="alert" style={{ padding: '36px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <SearchX size={28} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Lead not found</h1>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', maxWidth: 420 }}>
          There is no lead #{leadId}. Check the ID, or search by email or phone in the Lead Inspector.
        </p>
      </section>
    </div>
  )
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading lead" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="li-skeleton" style={{ height: 120 }} />
      <div className="li-grid">
        <div className="li-skeleton" style={{ height: 360 }} />
        <div className="li-skeleton" style={{ height: 360 }} />
      </div>
    </div>
  )
}
